/**
 * Memory State Service - In-Memory Projected State
 * 
 * Architecture:
 * - IndexedDB = Server truth (updated by events/snapshots)
 * - Memory = Projected state (server truth + pending commands)
 * - UI reads from memory state, not IndexedDB
 * 
 * Flow:
 * 1. Load base state from IndexedDB (server truth)
 * 2. Apply pending commands optimistically
 * 3. When events arrive: Update IndexedDB → Reload memory state
 * 4. When commands execute: Apply to memory immediately
 */

import { localDB } from '../../db/client'
import { getHandlerForCommand } from '../commands/handlers'
import type { Item, List, Scenario, ScenarioItem, Flow } from './mutations'
import { logger } from '../utils/logger'
import type { Command } from '../commands/types'

/**
 * Scenario load status - single source of truth for UI.
 * Managed by MemoryStateService, written by ScenarioHydrationCoordinator.
 */
export type ScenarioLoadStatus =
  | 'not-loaded'     // Not yet requested or data evicted
  | 'loading'        // Hydration in progress
  | 'loaded'         // Fully loaded in IndexedDB + Memory
  | 'error'          // Failed to load (network/unknown error)
  | 'access-denied'  // 403 from server
  | 'deleted'        // 404 from server / scenario deleted

interface MemoryState {
  scenarios: Map<string, Scenario>
  lists: Map<string, List>  // Lists contain their items embedded in list.items dictionary
  flows: Map<string, Flow>
  // NOTE: No separate 'items' Map - items are embedded in their parent lists
  // This ensures items with the same ID in different lists don't overwrite each other
  scenarioItems: Map<string, ScenarioItem[]> // scenarioId -> scenario items list
  pendingCommandIds: Set<string> // Track which commands are pending
  scenarioLoadStatus: Map<string, ScenarioLoadStatus> // Single source of truth for scenario lifecycle state
}

interface StateListener {
  scenarioId: string | null // null = listen to all scenarios
  callback: (scenarioId: string) => void
  needsFullData: boolean // true = needs lists/items, false = metadata only
}

class MemoryStateService {
  private state: MemoryState = {
    scenarios: new Map(),
    lists: new Map(),  // Lists contain their items embedded in list.items dictionary
    flows: new Map(),
    scenarioItems: new Map(),
    pendingCommandIds: new Set(),
    scenarioLoadStatus: new Map()
  }
  
  private listeners: StateListener[] = []

  /**
   * Get scenario items for a scenario
   */
  getScenarioItems(scenarioId: string): ScenarioItem[] {
    return this.state.scenarioItems.get(scenarioId) || []
  }

  /**
   * Check if a scenario is fully loaded in memory
   */
  isScenarioLoaded(scenarioId: string): boolean {
    return this.state.scenarioLoadStatus.get(scenarioId) === 'loaded'
  }

  /**
   * Get the load status for a scenario.
   * This is the single source of truth for UI to determine loading/error/access states.
   */
  getScenarioLoadStatus(scenarioId: string): ScenarioLoadStatus {
    return this.state.scenarioLoadStatus.get(scenarioId) || 'not-loaded'
  }

  /**
   * Set the load status for a scenario.
   * Called by ScenarioHydrationCoordinator to communicate lifecycle transitions.
   * Notifies listeners so UI reacts to status changes.
   */
  setScenarioLoadStatus(scenarioId: string, status: ScenarioLoadStatus): void {
    const oldStatus = this.state.scenarioLoadStatus.get(scenarioId)
    if (oldStatus === status) return
    
    logger.debug(`[MemoryState] Load status for ${scenarioId}: ${oldStatus || 'none'} → ${status}`)
    this.state.scenarioLoadStatus.set(scenarioId, status)
    this.notifyListeners(scenarioId)
  }

  /**
   * Mark a scenario as not fully loaded
   * (useful for forcing a full reload in tests)
   */
  markScenarioNotFullyLoaded(scenarioId: string): void {
    this.state.scenarioLoadStatus.set(scenarioId, 'not-loaded')
  }

  /**
   * Load a scenario from IndexedDB into memory
   * This loads the server truth (base state) and applies pending commands
   */
  async loadScenario(scenarioId: string): Promise<void> {
    logger.debug(`[MemoryState] Loading scenario ${scenarioId} from IndexedDB`)
    
    // Clear any stale state for this scenario before loading fresh data.
    // This is critical when loadScenario is called after a metadata-only reload
    // (e.g., ListDeleted event arrived while user was on homepage). Without this,
    // deleted lists/flows would persist as ghosts in memory.
    for (const [listId, list] of this.state.lists) {
      if (list.scenarioId === scenarioId) {
        this.state.lists.delete(listId)
      }
    }
    for (const [flowId, flow] of this.state.flows) {
      if (flow.scenarioId === scenarioId) {
        this.state.flows.delete(flowId)
      }
    }
    this.state.scenarioItems.delete(scenarioId)
    
    // Load scenario
    const scenario = await localDB.getScenario(scenarioId)
    if (!scenario) {
      throw new Error(`Scenario ${scenarioId} not found in IndexedDB`)
    }
    
    this.state.scenarios.set(scenarioId, scenario as Scenario)
    
    // Load all lists for this scenario (lists contain their items embedded in list.items dictionary)
    const lists = await localDB.getLists(scenarioId)
    let totalItemCount = 0
    for (const list of lists) {
      this.state.lists.set(list.id, list as List)
      totalItemCount += list.items ? Object.keys(list.items).length : 0
    }
    logger.info(`[MemoryState] Loaded ${lists.length} lists with ${totalItemCount} total items`)
    
    // Load all flows for this scenario
    const flows = await localDB.getFlows(scenarioId)
    for (const flow of flows) {
      this.state.flows.set(flow.id, flow as Flow)
    }
    
    // NOTE: Items are NOT loaded separately - they stay embedded in their parent lists
    // This ensures items with the same ID in different lists don't overwrite each other
    
    logger.debug(`[MemoryState] Loaded scenario ${scenarioId}: ${lists.length} lists, ${flows.length} flows, ${totalItemCount} items`)

    // Load scenario items
    const scenarioItemsDoc = await localDB.getScenarioItems(scenarioId)
    if (scenarioItemsDoc) {
      const normalized = (scenarioItemsDoc.items || []).map((s: any) => ({
        ...s,
        quantity: typeof s?.quantity === 'number' && s.quantity >= 1 ? s.quantity : 1
      }))
      this.state.scenarioItems.set(scenarioId, normalized)
    }

    // Mark as fully loaded
    this.state.scenarioLoadStatus.set(scenarioId, 'loaded')
    
    // Apply pending commands on top of base state (optimistic updates)
    const allPendingCommands = await localDB.getSyncQueue()
    const pendingCommands = allPendingCommands.filter(cmd => cmd.command.scenarioId === scenarioId)
    
    for (const queueItem of pendingCommands) {
      if (queueItem.command) {
        await this.applyCommandOptimistically(queueItem.command)
      }
    }
    
    logger.debug(`[MemoryState] Applied ${pendingCommands.length} pending commands to scenario ${scenarioId}`)
    
    // Notify listeners that data has been loaded
    this.notifyListeners(scenarioId)
  }

  /**
   * Load all scenarios from IndexedDB into memory
   * Used by scenarios list page
   */
  async loadAllScenarios(): Promise<void> {
    logger.debug('[MemoryState] Loading all scenarios from IndexedDB')
    
    const scenarios = await localDB.getScenarios()
    for (const scenario of scenarios) {
      this.state.scenarios.set(scenario.id, scenario as Scenario)
    }
    
    logger.debug(`[MemoryState] Loaded ${scenarios.length} scenarios`)
  }

  /**
   * Apply pending scenario-level commands on top of loaded scenarios
   * Only applies commands that affect scenario metadata (Create, Rename, Delete)
   * Skips list/item commands for performance (homepage doesn't need those)
   */
  async applyPendingScenarioCommands(): Promise<void> {
    const allPendingCommands = await localDB.getSyncQueue()
    
    // Filter to only scenario-level commands
    const scenarioCommands = allPendingCommands.filter(cmd => 
      cmd.command.commandType === 'CreateScenario' ||
      cmd.command.commandType === 'RenameScenario' ||
      cmd.command.commandType === 'DeleteScenario' ||
      cmd.command.commandType === 'LeaveScenario'
    )

    if (scenarioCommands.length === 0) return

    logger.debug(`[MemoryState] Applying ${scenarioCommands.length} pending scenario commands`)
    
    for (const queueItem of scenarioCommands) {
      if (queueItem.command) {
        await this.applyCommandOptimistically(queueItem.command)
      }
    }
  }

  /**
   * Reload a scenario from IndexedDB (after events update the base state)
   * Intelligently reloads based on what current subscribers need
   */
  async reloadScenario(scenarioId: string): Promise<void> {
    logger.debug(`[MemoryState] Reloading scenario ${scenarioId} from IndexedDB`)
    
    // Check if any active listeners need full data for this scenario
    const needsFullData = this.listeners.some(
      listener => listener.needsFullData && 
      (listener.scenarioId === scenarioId || listener.scenarioId === null)
    )
    
    if (!needsFullData) {
      // Only metadata needed - just reload scenario header and pending scenario commands
      logger.debug(`[MemoryState] Reloading metadata only for scenario ${scenarioId}`)
      const scenario = await localDB.getScenario(scenarioId)
      if (scenario) {
        logger.debug(`[MemoryState] Loaded from IndexedDB:`, { name: scenario.name, description: scenario.description })
        this.state.scenarios.set(scenarioId, scenario as Scenario)
      } else {
        // Scenario gone from IndexedDB (e.g. deleted/left by another tab/device)
        this.state.scenarios.delete(scenarioId)
      }
      
      // Reload scenario items (lightweight metadata)
      const scenarioItemsDoc = await localDB.getScenarioItems(scenarioId)
      if (scenarioItemsDoc) {
        const normalized = (scenarioItemsDoc.items || []).map((s: any) => ({
          ...s,
          quantity: typeof s?.quantity === 'number' && s.quantity >= 1 ? s.quantity : 1
        }))
        this.state.scenarioItems.set(scenarioId, normalized)
      } else {
        this.state.scenarioItems.delete(scenarioId)
      }
      
      // Reapply pending scenario-level commands
      const allPendingCommands = await localDB.getSyncQueue()
      const scenarioCommands = allPendingCommands.filter(cmd => 
        cmd.command.scenarioId === scenarioId &&
        (cmd.command.commandType === 'CreateScenario' || 
         cmd.command.commandType === 'RenameScenario' || 
         cmd.command.commandType === 'DeleteScenario' ||
         cmd.command.commandType === 'LeaveScenario')
      )
      
      for (const queueItem of scenarioCommands) {
        if (queueItem.command) {
          await this.applyCommandOptimistically(queueItem.command)
        }
      }

      // Important: Since we only reloaded metadata, we must assume the list data in memory is stale.
      // We mark it as NOT fully loaded so the next UI access forces a full reload.
      this.state.scenarioLoadStatus.set(scenarioId, 'not-loaded')
      
      this.notifyListeners(scenarioId)
      return
    }
    
    // Full reload needed - get pending commands before clearing state
    const allPendingCommands = await localDB.getSyncQueue()
    const pendingCommands = allPendingCommands.filter(cmd => cmd.command.scenarioId === scenarioId)
    
    // Clear current state for this scenario
    // (loadScenario will also clear, but we clear the scenario header here
    // since loadScenario doesn't delete it before re-setting it)
    this.state.scenarios.delete(scenarioId)

    // Reload base state from IndexedDB
    // loadScenario clears stale lists/flows/scenarioItems internally
    await this.loadScenario(scenarioId)
    
    // Reapply pending commands (optimistic updates)
    for (const queueItem of pendingCommands) {
      if (queueItem.command) {
        await this.applyCommandOptimistically(queueItem.command)
      }
    }
    
    // Notify listeners
    this.notifyListeners(scenarioId)
  }

  /**
   * Apply a command optimistically to memory state
   * NOTE: Assumes scenario is already loaded in memory (except for CreateScenario)
   */
  async applyCommandOptimistically(command: Command): Promise<void> {
    logger.debug(`[MemoryState] Applying command optimistically: ${command.commandType}`)
    
    // Special case: CreateScenario needs to load the scenario first
    if (command.commandType === 'CreateScenario') {
      const scenario = this.state.scenarios.get(command.scenarioId)
      if (!scenario) {
        try {
          await this.loadScenario(command.scenarioId)
        } catch (error) {
          // Scenario not in IndexedDB - likely already deleted or never saved
          logger.warn(`[MemoryState] Skipping CreateScenario for ${command.scenarioId} - not found in IndexedDB`, error)
          return
        }
      }
    }
    
    // Track that this command is pending
    this.state.pendingCommandIds.add(command.commandId)
    
    // Execute handler (mutates state directly)
    const handler = getHandlerForCommand(command.commandType)
    await handler(command, this.state)
    
    // Notify listeners of state change
    this.notifyStateChange(command.scenarioId)
  }

  /**
   * Apply command to memory state (public API for command execution)
   * Loads scenario if needed, then applies command optimistically
   */
  async applyCommand(command: Command): Promise<void> {
    logger.debug(`[MemoryState] Applying command: ${command.commandType}`)
    
    // Ensure scenario is loaded in memory
    const scenario = this.state.scenarios.get(command.scenarioId)
    if (!scenario) {
      await this.loadScenario(command.scenarioId)
    }
    
    // Track that this command is pending
    this.state.pendingCommandIds.add(command.commandId)
    
    // Execute handler (mutates state directly)
    const handler = getHandlerForCommand(command.commandType)
    await handler(command, this.state)
    
    // Notify listeners of state change
    this.notifyStateChange(command.scenarioId)
  }

  /**
   * Remove a command from pending (when its event arrives)
   */
  removeCommand(commandId: string): void {
    this.state.pendingCommandIds.delete(commandId)
  }

  /**
   * Get state (read-only access)
   */
  getScenario(scenarioId: string): Scenario | undefined {
    return this.state.scenarios.get(scenarioId)
  }

  /**
   * Get all scenarios from memory state
   */
  getAllScenarios(): Scenario[] {
    return Array.from(this.state.scenarios.values())
  }

  /**
   * Get scenario with lists and items (for UI projection)
   * Items are already embedded in each list's items dictionary - no rebuilding needed
   */
  getScenarioWithData(scenarioId: string): (Scenario & { lists: List[] }) | undefined {
    const scenario = this.state.scenarios.get(scenarioId)
    if (!scenario) return undefined

    // Lists already have their items embedded - just return them directly
    const lists = this.getListsByScenario(scenarioId)

    return {
      ...scenario,
      lists
    }
  }

  getList(listId: string): List | undefined {
    return this.state.lists.get(listId)
  }

  /**
   * Get an item by ID from a specific list
   * Since items are embedded in lists, we need the listId to find it
   */
  getItem(listId: string, itemId: string): Item | undefined {
    const list = this.state.lists.get(listId)
    return list?.items?.[itemId]
  }

  getListsByScenario(scenarioId: string): List[] {
    const lists: List[] = []
    for (const list of this.state.lists.values()) {
      if (list.scenarioId === scenarioId) {
        lists.push(list)
      }
    }
    return lists.sort((a, b) => (a.order || 0) - (b.order || 0))
  }

  getFlow(flowId: string): Flow | undefined {
    return this.state.flows.get(flowId)
  }

  getFlowsByScenario(scenarioId: string): Flow[] {
    const flows: Flow[] = []
    for (const flow of this.state.flows.values()) {
      if (flow.scenarioId === scenarioId) {
        flows.push(flow)
      }
    }
    return flows
  }

  /**
   * Get items for a list - items are embedded in the list's items dictionary
   */
  getItemsByList(listId: string): Item[] {
    const list = this.state.lists.get(listId)
    if (!list?.items) return []
    
    const items = Object.values(list.items)
    return items.sort((a, b) => a.order - b.order)
  }

  /**
   * Direct state mutations (used by command handlers)
   * For internal use by command system only
   */
  setScenario(scenario: Scenario): void {
    this.state.scenarios.set(scenario.id, scenario)
    this.notifyListeners(scenario.id)
  }
  
  setList(list: List): void {
    this.state.lists.set(list.id, list)
    this.notifyListeners(list.scenarioId)
  }
  
  /**
   * Set/update an item within its parent list
   */
  setItemInList(listId: string, item: Item): void {
    const list = this.state.lists.get(listId)
    if (!list) {
      logger.warn(`[MemoryState] Cannot set item - list ${listId} not found`)
      return
    }
    
    const updatedList = {
      ...list,
      items: {
        ...list.items,
        [item.id]: item
      }
    }
    this.state.lists.set(listId, updatedList)
    this.notifyListeners(list.scenarioId)
  }
  
  deleteList(_scenarioId: string, listId: string): void {
    const list = this.state.lists.get(listId)
    if (list) {
      this.state.lists.delete(listId)
      this.notifyListeners(list.scenarioId)
    }
  }
  
  /**
   * Delete an item from its parent list
   */
  deleteItemFromList(listId: string, itemId: string): void {
    const list = this.state.lists.get(listId)
    if (!list?.items?.[itemId]) return
    
    const { [itemId]: _, ...remainingItems } = list.items
    const updatedList = {
      ...list,
      items: remainingItems
    }
    this.state.lists.set(listId, updatedList)
    this.notifyListeners(list.scenarioId)
  }

  /**
   * Mark a command as pending (used by command executor)
   */
  markCommandPending(scenarioId: string, commandId: string): void {
    this.state.pendingCommandIds.add(commandId)
    this.notifyListeners(scenarioId)
  }

  /**
   * Notify listeners of state changes (used by command handlers)
   */
  notifyStateChange(scenarioId: string): void {
    this.notifyListeners(scenarioId)
  }

  /**
   * Listen to state changes for a scenario
   * @param scenarioId - Scenario to listen to, or null for all scenarios
   * @param callback - Function to call on state changes
   * @param needsFullData - Whether listener needs lists/items (true) or just metadata (false)
   */
  onStateChange(
    scenarioId: string | null, 
    callback: (scenarioId: string) => void,
    needsFullData: boolean = true
  ): () => void {
    const listener: StateListener = { scenarioId, callback, needsFullData }
    this.listeners.push(listener)
    
    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(listener)
      if (index > -1) {
        this.listeners.splice(index, 1)
      }

      // Check if any listeners remain for this scenario with full data needs
      /* 
       * OPTIMIZATION: Do NOT unload data when listeners disconnect.
       * Keeping data in memory significantly improves navigation speed when returning to a scenario.
       * The memory footprint for text-based lists is negligible for modern browsers.
       * 
       * Original code:
       * if (scenarioId) {
       *   const remainingFullDataListeners = this.listeners.some(l => 
       *     l.needsFullData && (l.scenarioId === scenarioId || l.scenarioId === null)
       *   )
       *   if (!remainingFullDataListeners) {
       *     this.unloadScenarioData(scenarioId)
       *   }
       * }
       */
    }
  }

  private notifyListeners(scenarioId: string): void {
    for (const listener of this.listeners) {
      // Notify if listening to this scenario or all scenarios
      if (listener.scenarioId === null || listener.scenarioId === scenarioId) {
        try {
          listener.callback(scenarioId)
        } catch (error) {
          logger.error('[MemoryState] Error in listener:', error)
        }
      }
    }
  }

  /**
   * Notify all listeners that listen to all scenarios (scenarioId = null)
   * Used when multiple scenarios are updated (e.g., initial load)
   */
  notifyAllListeners(): void {
    for (const listener of this.listeners) {
      if (listener.scenarioId === null) {
        try {
          // Pass empty string as scenarioId since these listeners handle all scenarios
          listener.callback('')
        } catch (error) {
          logger.error('[MemoryState] Error in listener:', error)
        }
      }
    }
  }

  /**
   * Clear all state (used for logout, etc.)
   * Items are embedded in lists, so clearing lists also clears items
   */
  clear(): void {
    this.state.scenarios.clear()
    this.state.lists.clear()
    this.state.flows.clear()
    this.state.scenarioItems.clear()
    this.state.pendingCommandIds.clear()
    this.state.scenarioLoadStatus.clear()
  }
}

export const memoryStateService = new MemoryStateService()
